import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { DesignIssuesComponent } from '../../design-issues/design-issues.component';
import {
  lucideCheck,
  lucideCopy,
  lucideCopyPlus,
  lucideDownload,
  lucideEllipsis,
  lucidePencil,
  lucideTrash2,
} from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSheetImports } from '@spartan-ng/helm/sheet';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import {
  activationBlocker,
  otherVoucherType,
  ptToMm,
  VoucherDesign,
  VoucherType,
  voucherTypeLabel,
} from '../../../../../../services/voucher-design.service';
import { DesignThumbnailComponent } from '../../design-thumbnail/design-thumbnail.component';

const SOURCE_LABELS: Record<string, string> = { pdf: 'PDF', svg: 'SVG', image: 'Bild' };

/**
 * One design in its voucher-type group. A click anywhere on the card makes it
 * the printed design (a stretched button under the content); a white check on
 * the primary colour marks the design in use. Only "Bearbeiten" opens the editor.
 */
@Component({
  selector: 'app-design-card',
  standalone: true,
  imports: [
    DesignIssuesComponent,
    RouterModule,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmDropdownMenuImports,
    HlmSeparatorImports,
    HlmSheetImports,
    HlmSpinnerImports,
    HlmTypographyImports,
    DesignThumbnailComponent,
  ],
  providers: [
    provideIcons({
      lucideCheck,
      lucideCopy,
      lucideCopyPlus,
      lucideDownload,
      lucideEllipsis,
      lucidePencil,
      lucideTrash2,
    }),
  ],
  templateUrl: './design-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'relative flex gap-3 rounded-lg border p-3 transition-colors sm:flex-col sm:gap-2',
    '[class.border-primary]': 'isActive()',
    '[class.ring-1]': 'isActive()',
    '[class.ring-primary]': 'isActive()',
    '[class.hover:bg-muted/50]': 'selectable',
  },
})
export class DesignCardComponent {
  readonly design = input.required<VoucherDesign>();
  /** This design is printed for its voucher type. */
  readonly isActive = input(false);
  /** Some design request is running: actions wait. */
  readonly busy = input(false);
  /** The running request is for this design. */
  readonly working = input(false);
  /** A sample PDF is being made (for any design). */
  readonly previewBusy = input(false);
  /** The sample PDF is being made for this design. */
  readonly previewing = input(false);

  readonly activate = output<VoucherDesign>();
  readonly copy = output<{ design: VoucherDesign; voucherType: VoucherType }>();
  readonly delete = output<VoucherDesign>();
  readonly preview = output<VoucherDesign>();

  readonly sheetOpen = signal(false);
  private pendingAction: (() => void) | null = null;

  get typeLabel(): string {
    return voucherTypeLabel(this.design().voucher_type);
  }

  /** Why the design cannot be used yet, or null when it can. */
  get blocker(): string | null {
    return activationBlocker(this.design());
  }

  /** A click on the card would switch to this design. */
  get selectable(): boolean {
    return !this.isActive() && !this.blocker && !this.busy();
  }

  /** What the card's button does, for screen readers. */
  get selectLabel(): string {
    const name = `„${this.design().name}“`;
    if (this.isActive()) return `${name} wird für ${this.typeLabel}e gedruckt`;
    const blocker = this.blocker;
    if (blocker) return `${name} kann noch nicht verwendet werden: ${blocker}`;
    return `${name} für ${this.typeLabel}e verwenden`;
  }

  get copyLabel(): string {
    return this.design().voucher_type === 'money'
      ? 'Als Produkt-Design kopieren'
      : 'Als Wert-Design kopieren';
  }

  get sizeLabel(): string {
    const d = this.design();
    const size = `${ptToMm(d.page_width_pt)} × ${ptToMm(d.page_height_pt)} mm`;
    if (d.source_kind) return `${size} · ${SOURCE_LABELS[d.source_kind] ?? d.source_kind}`;
    return `${size} · ${d.background_kind === 'template' ? 'Vorlage' : 'Leere Seite'}`;
  }

  onActivate() {
    if (this.selectable) this.activate.emit(this.design());
  }

  onPreview() {
    this.preview.emit(this.design());
  }

  onCopySame() {
    this.copy.emit({ design: this.design(), voucherType: this.design().voucher_type });
  }

  onCopyOther() {
    this.copy.emit({
      design: this.design(),
      voucherType: otherVoucherType(this.design().voucher_type),
    });
  }

  onDelete() {
    this.delete.emit(this.design());
  }

  /** Runs after the sheet has closed, so its focus restore can't steal the next dialog's focus. */
  chooseAction(action: () => void) {
    this.pendingAction = action;
    this.sheetOpen.set(false);
  }

  runPendingAction() {
    const action = this.pendingAction;
    this.pendingAction = null;
    action?.call(this);
  }
}
